import { PACE_CODES_TIME_LIFTS, type PaceRefusalCode, type SuppressionReason } from '@norum/contracts';
import type {
  CampaignEndReason,
  CampaignState,
  LifecycleBasis,
  LifecycleEvidence,
  LifecycleSyncState,
  MailingEndReason,
  MailingState,
  ProspectExclusion,
  ProspectStage,
  WireFitReason,
} from '@/api/wire';

/**
 * Why a first touch may not leave now, as a person reads it.
 *
 * Keyed by the platform's own list, so a code added there without words
 * here fails `test/outreach.test.tsx` rather than reaching a screen as
 * `snake_case`. The sentence under each title is still the platform's
 * `reason`, shown as it came; this is only the heading, and who can move
 * it — time, the lead, or nobody here.
 */
export const PACE_WORDS: Record<PaceRefusalCode, { title: string; who: 'time' | 'a person' | 'nobody' }> = {
  market_not_declared: { title: 'This country is not one we write to yet', who: 'a person' },
  legal_basis_unconfirmed: { title: 'Nobody has confirmed the law here yet', who: 'a person' },
  consent_required: { title: 'Here, a first email needs their say-so first', who: 'nobody' },
  market_disabled: { title: 'Writing to this country is switched off', who: 'a person' },
  personal_address_not_permitted: { title: 'Here, only a shared address like info@ may be written to', who: 'nobody' },
  zone_not_in_market: { title: 'That time zone is not in this country', who: 'a person' },
  weekend: { title: 'Not on a weekend', who: 'time' },
  public_holiday: { title: 'Not on a public holiday', who: 'time' },
  quiet_period: { title: 'Not in the quiet weeks', who: 'time' },
  outside_window: { title: 'Not at this hour', who: 'time' },
  daily_ceiling: { title: 'Enough for today', who: 'time' },
  too_soon: { title: 'Too soon after the last one', who: 'time' },
  no_slot_within_horizon: { title: 'No working hour in the next two months', who: 'a person' },
};

/** Whether waiting is the whole remedy: shown as "not yet", never as a red refusal. */
export const liftsWithTime = (code: PaceRefusalCode): boolean => PACE_CODES_TIME_LIFTS.has(code);

export const SUPPRESSION_WORDS: Record<SuppressionReason, string> = {
  unsubscribed: 'Unsubscribed',
  objected: 'Asked us to stop',
  bounced_hard: 'The address does not exist',
  complained: 'Marked it as spam',
  manual: 'Added by a person',
};

export const LEGAL_BASIS_WORDS = {
  opt_out_b2b: 'Businesses may be written to, with a way to say no on every email',
  generic_address_only: 'Only shared addresses such as info@ or reservations@',
  consent_required: 'Only people who said yes first',
  unconfirmed: 'Not confirmed by anybody yet',
} as const;

/**
 * "Monday 28 September, 09:30 in Copenhagen (08:30 your time)" — when a
 * pause lifts, in the country's own clock, which is the one that decides
 * it, and in the reader's, which is the one they plan by (FRD §G).
 */
export function resumesIn(iso: string, zone: string, viewerZone?: string): string {
  const at = new Date(iso);
  const day = at.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: zone });
  const clock = (timeZone: string | undefined) =>
    at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
  const city = zone.split('/').pop()!.replace(/_/g, ' ');
  const theirs = clock(zone);
  const yours = clock(viewerZone);
  return `${day}, ${theirs} in ${city}${yours === theirs ? '' : ` (${yours} your time)`}`;
}

/**
 * The platform's reason with its references taken out.
 *
 * The sentence is written for the audit record as well as the screen, and
 * carries "(ADR-0022)" and "(W6)" for the first. A person reads the rest of
 * it unchanged; the reference is on the decision page for anyone who wants
 * it, and nothing else in the sentence is rewritten here.
 */
export function withoutReferences(reason: string): string {
  return reason
    .replace(/\s*\((?:ADR-\d{4}|W\d+|§\d+|amendment \d{4})(?:[,;]\s*(?:ADR-\d{4}|W\d+|§\d+|\d{4}))*(?:,\s*proposed)?\)/gi, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Where a restaurant is on the way to a first email (ADR-0064). */
export const STAGE_WORDS: Record<ProspectStage, string> = {
  discovered: 'Found — its website is read next',
  read: 'Read — scored from its website',
  enriched: 'Has a checked address',
  drafted: 'A first email is waiting in the shared box',
  excluded: 'Taken off the list',
};

export const STAGE_CHIP: Record<ProspectStage, { label: string; tone: 'accent' | 'warning' | 'danger' | 'neutral' }> = {
  discovered: { label: 'FOUND', tone: 'neutral' },
  read: { label: 'READ', tone: 'neutral' },
  enriched: { label: 'ADDRESS', tone: 'accent' },
  drafted: { label: 'DRAFTED', tone: 'accent' },
  excluded: { label: 'OFF THE LIST', tone: 'danger' },
};

export const EXCLUSION_WORDS: Record<ProspectExclusion, string> = {
  by_person: 'A person took it off',
  not_operational: 'It is not open for business',
  no_site: 'It has no website to read',
  weak_fit: 'It does not look like a kitchen for us',
  suppressed: 'Its address asked us to stop',
  no_address: 'No shared address could be found for it',
  undeliverable: 'Its address would bounce',
  market_closed: 'Its country is not open to a first email',
  robots: 'Its site asks not to be read',
  natural_person: 'Its register says it is a sole trader, a person rather than a company',
};

const TERM_WORDS: Record<string, string> = {
  black_allium: 'black garlic or black onion',
  fermentation: 'fermentation',
  tasting_menu: 'a tasting menu',
  koji: 'koji',
  miso: 'miso',
  kimchi: 'kimchi',
  umami: 'umami',
};

const REASON_WORDS: Record<string, string> = {
  place_type: 'The kind of place it is',
  price_level: 'Its price level',
  rating: 'How it is rated',
  too_few_ratings: 'Too few ratings to count',
  serves_dinner: 'It serves dinner',
  not_operational: 'Not operating',
};

/**
 * One reason a restaurant scored what it did. A site reason quotes the
 * restaurant's own page; a reason from Google says only what kind of fact
 * it was, because the fact itself is not kept — the link opens it live.
 */
export function reasonWords(r: WireFitReason): string {
  // A profile's rule and a signal carry their own words as the evidence (PR-12).
  if (r.code.startsWith('rule_') || r.code.startsWith('signal_')) return r.evidence || r.code.slice(r.code.indexOf('_') + 1).replace(/_/g, ' ');
  if (r.code.startsWith('site_')) {
    const term = TERM_WORDS[r.code.slice(5)] ?? r.code.slice(5).replace(/_/g, ' ');
    return r.evidence ? `Its menu mentions ${term} (“${r.evidence}”)` : `Its menu mentions ${term}`;
  }
  return REASON_WORDS[r.code] ?? r.code.replace(/_/g, ' ');
}

/** How a restaurant is reached, as a chip and a sentence (S:13). */
export const WAY_IN_WORDS: Record<'introduced' | 'asked' | 'warm' | 'cold', { label: string; tone: 'accent' | 'neutral' | 'warning'; says: string }> = {
  introduced: { label: 'Introduced', tone: 'accent', says: 'a colleague has already introduced us' },
  asked: { label: 'Intro asked', tone: 'accent', says: 'a colleague has been asked for an introduction' },
  warm: { label: 'Warm', tone: 'accent', says: 'someone here already knows people there' },
  cold: { label: 'Cold', tone: 'neutral', says: 'nobody here knows them yet' },
};

/** A campaign's state, as a chip (FR-OUT-73). */
export const CAMPAIGN_CHIP: Record<CampaignState, { label: string; tone: 'accent' | 'warning' | 'danger' | 'neutral' }> = {
  draft: { label: 'DRAFT', tone: 'neutral' },
  approved: { label: 'APPROVED', tone: 'accent' },
  ended: { label: 'ENDED', tone: 'danger' },
};

export const CAMPAIGN_END_WORDS: Record<CampaignEndReason, string> = {
  by_person: 'A person ended it',
  breach: 'Stopped by a threshold agreed in advance',
  expired: 'Its dates ran out',
  ceiling: 'It sent as many as it was approved for',
};

export const MAILING_CHIP: Record<MailingState, { label: string; tone: 'accent' | 'warning' | 'danger' | 'neutral' }> = {
  draft: { label: 'DRAFT', tone: 'neutral' },
  approved: { label: 'APPROVED', tone: 'accent' },
  ended: { label: 'ENDED', tone: 'danger' },
};

export const MAILING_END_WORDS: Record<MailingEndReason, string> = {
  by_person: 'A person ended it',
  breach: 'Stopped by a threshold agreed in advance',
  expired: 'Its dates ran out',
  ceiling: 'It sent as many as it was approved for',
};

/** What came of a journey's ask (ADR-0099). */
export const REQUEST_CHIP: Record<'asked' | 'sent' | 'refused', { label: string; tone: 'accent' | 'warning' | 'danger' | 'neutral' }> = {
  asked: { label: 'WAITING', tone: 'neutral' },
  sent: { label: 'SENT', tone: 'accent' },
  refused: { label: 'REFUSED', tone: 'warning' },
};

/** Why Customer.io may be told about someone (ADR-0082). */
export const BASIS_WORDS: Record<LifecycleBasis, { title: string; hint: string }> = {
  opted_in: { title: 'Said yes to hearing from us', hint: 'Any lifecycle email, newsletters included.' },
  requested: { title: 'Asked for something', hint: 'Only what they asked about: a sample, its shipping, its arrival.' },
  customer: { title: 'Bought from us', hint: 'Only what an order needs: its confirmation and its shipping.' },
};

/** What a basis was recorded from. */
export const EVIDENCE_WORDS: Record<LifecycleEvidence, string> = {
  message: 'A message they sent us',
  order: 'An order',
  csv: 'A list, line by line',
  person: 'A person’s written word',
  form: 'A form on our site',
};

/** Where a person stands with Customer.io. */
export const SYNC_CHIP: Record<LifecycleSyncState, { label: string; tone: 'accent' | 'warning' | 'danger' | 'neutral' }> = {
  none: { label: 'NOT SENT', tone: 'neutral' },
  identified: { label: 'IN CUSTOMER.IO', tone: 'accent' },
  unsubscribed: { label: 'UNSUBSCRIBED THERE', tone: 'warning' },
  erasing: { label: 'BEING ERASED', tone: 'danger' },
  deleted: { label: 'ERASED', tone: 'danger' },
};

export const LEAD_WORDS = {
  sample: 'Wants a sample',
  wholesale: 'Wholesale',
  customer: 'Customer',
  newsletter: 'Newsletter',
} as const;

/** The events the platform tells Customer.io about, in a person's words (ADR-0082). The list is the platform's; nothing else can be sent. */
export const LIFECYCLE_EVENT_WORDS = {
  sample_requested: 'They asked for a sample',
  sample_shipped: 'Their sample was shipped',
  sample_delivered: 'Their sample was delivered',
  wholesale_approved: 'Their wholesale account was approved',
  order_completed: 'They completed an order',
  order_shipped: 'Their order was shipped',
} as const;
export type LifecycleEventName = keyof typeof LIFECYCLE_EVENT_WORDS;
